import { useState } from 'react';
import { formatCurrency } from '@/lib/format.js';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { api } from '@/lib/api.js';

// THE RATE DOOR'S SECOND HANDLE (David 2026-10-01). PATCH /api/sweep/:id/rate
// is the one writer of commission_rate and it accepts any live, unswept row;
// its only sender was the sweep page, which lists a row once its payment has
// SUCCEEDED and never lists a financed one. So a deferred, pending or financed
// bind had no screen where its commission could be stated (Aspire Leasing,
// premium financed at 12.5%). This sends the same payload to the same door
// from the deal's own ledger. It writes nothing itself and derives nothing:
// the stated line renders what the row stores, with cents whenever the figure
// has cents (formatCurrency once rounded 161.55 to $162).
//
// Both fields start EMPTY (guard: tests/sweep-no-prefill.test.js walks this
// folder). Handle guard: tests/commission-rate-handle.test.js.

const SHARE_OPTIONS = [
  { value: '', label: 'choose share' },
  { value: '1', label: 'full (1.0)' },
  { value: '0.5', label: 'co-brokered (0.5)' },
];

// A row the rate door will take: live, not yet swept, with a premium for the
// rate to apply to. Payment status is deliberately NOT part of this.
export function canStateRate(t) {
  return !!t && !t.voided_at && !t.swept_at && t.type !== 'correction' && t.premium_delta != null;
}

export default function CommissionRateInline({ txn, onSaved, label }) {
  const [open, setOpen] = useState(false);
  const [ratePct, setRatePct] = useState('');
  const [share, setShare] = useState('');
  const [busy, setBusy] = useState(false);

  const stated = txn.commission_rate != null;

  const save = async () => {
    const pct = Number(ratePct);
    if (ratePct === '' || !Number.isFinite(pct) || pct <= 0 || pct > 100) { toast.error('Rate is a percentage from the binder, e.g. 15 or 12.5.'); return; }
    if (share === '') { toast.error('Choose the share: full, or co-brokered.'); return; }
    setBusy(true);
    try {
      await api.patch(`/api/sweep/${encodeURIComponent(txn.id)}/rate`, { commission_rate: pct / 100, commission_share: Number(share) });
      toast.success(`Rate stated: ${pct}% at ${share}.`);
      setOpen(false);
      setRatePct('');
      setShare('');
      await onSaved?.(); // a confirmation is a read back, not an echo
    } catch (e) {
      toast.error(e.body?.detail || e.message || 'Could not save the rate');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1 font-mono text-[11px] text-text-dim">
      {label ? <span className="mr-2 text-text-muted">{label}</span> : null}
      {stated ? (
        <span>
          Commission {(Number(txn.commission_rate) * 100).toFixed(2)}% at share {txn.commission_share}
          {txn.commission_delta != null ? <> = {formatCurrency(txn.commission_delta)}</> : null}
          {txn.net_to_carrier_delta != null ? <> · net to carrier {formatCurrency(txn.net_to_carrier_delta)}</> : null}
        </span>
      ) : (
        <span className="text-warning">Commission not stated</span>
      )}
      {canStateRate(txn) && !open ? (
        <button type="button" onClick={() => setOpen(true)} className="ml-2 underline-offset-2 hover:text-text hover:underline">
          {stated ? 'change rate' : 'state rate'}
        </button>
      ) : null}
      {canStateRate(txn) && open ? (
        <div className="mt-1.5 flex flex-wrap items-end gap-2 font-sans">
          <label className="text-xs text-text-muted">
            Rate % (binder)
            <Input
              type="number"
              step="0.01"
              inputMode="decimal"
              data-money-input="commission_rate"
              value={ratePct}
              onChange={(e) => setRatePct(e.target.value)}
              className="mt-1 h-8 w-24 text-xs"
            />
          </label>
          <label className="text-xs text-text-muted">
            Share
            <select
              data-money-input="commission_share"
              value={share}
              onChange={(e) => setShare(e.target.value)}
              className="mt-1 h-8 rounded-card border border-border bg-bg px-2 text-xs text-text"
            >
              {SHARE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <Button size="sm" onClick={save} disabled={busy}>{busy ? 'Saving...' : 'State rate'}</Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
        </div>
      ) : null}
    </div>
  );
}
